import { areUserRolesIn } from '../../../../helpers';
import {
  DOWNLOAD_TP_ROLES,
  PLAN_CREATOR_ROLES,
  PLAN_DELETE_ROLES,
  PLAN_EDITOR_ROLES,
  PROGRESS_REPORT_ROLES,
  TP_CONCLUDED_STATUSES,
  TP_DOWNLOADABLE_STATUSES,
  TP_NOT_EDITABLE_STATUSES,
  TP_OPTIONS_MENU_ITEM_IDS,
  TP_OPTIONS_SUBMENU_IDS,
  TP_REPORTABLE_STATUSES,
  TREATMENT_PLAN_V3_STATUS,
} from './constants';

import type {
  IClient,
  IOption,
  ITreatmentPlan,
  ITreatmentPlanStatusIdByLabel,
  ITrialSummary,
} from '../../../../utils/types';
import type {
  HeaderDialog,
  HeaderDialogCopy,
  HeaderDialogCopyContext,
  NoActivePlanMessageParams,
  PlanFlags,
  TpOptionsMenuEntry,
  TpOptionsRowContext,
  TpOptionsRowGates,
  TreatmentPlanV3Status,
  TreatmentPlanWithId,
} from './types';

export function getTpRouteSegment(pathname: string): string {
  return pathname.split('/')[3] ?? '';
}

export function toClientOption(client: IClient): IOption {
  return { id: client.id, label: `${client.first_name} ${client.last_name}` };
}

export function hasPlanStatus(
  plan: ITreatmentPlan | null,
  tpStatusIdByLabel: ITreatmentPlanStatusIdByLabel,
  statuses: ReadonlyArray<TreatmentPlanV3Status>,
): boolean {
  if (!plan || plan.status_id === undefined) return false;
  return statuses.some((status) => plan.status_id === tpStatusIdByLabel[status]);
}

export function derivePlanFlags(
  plans: ITreatmentPlan[],
  tpStatusIdByLabel: ITreatmentPlanStatusIdByLabel,
): PlanFlags {
  if (tpStatusIdByLabel[TREATMENT_PLAN_V3_STATUS.COMPLETED] === undefined || !plans.length) {
    return { hasNoConcludedTP: true, showTpActions: false, inProgressTpId: null };
  }
  const hasNoConcludedTP = !plans.some((plan) =>
    hasPlanStatus(plan, tpStatusIdByLabel, [TREATMENT_PLAN_V3_STATUS.COMPLETED]),
  );
  const showTpActions = plans.some((plan) =>
    hasPlanStatus(plan, tpStatusIdByLabel, TP_CONCLUDED_STATUSES),
  );
  const inProgressTpId =
    plans.find((plan) => hasPlanStatus(plan, tpStatusIdByLabel, [TREATMENT_PLAN_V3_STATUS.STARTED]))
      ?.id ?? null;
  return { hasNoConcludedTP, showTpActions, inProgressTpId };
}

export function getNoActivePlanMessage(params: NoActivePlanMessageParams): string {
  const {
    treatmentPlan,
    treatmentPlans,
    activeTreatmentPlan,
    tpStatusIdByLabel,
    isPlansLoading,
    selectedClient,
  } = params;
  if (!treatmentPlan || !treatmentPlans.length) return '';
  const hasConcludedPlan = treatmentPlans.some((plan) =>
    hasPlanStatus(plan, tpStatusIdByLabel, [TREATMENT_PLAN_V3_STATUS.COMPLETED]),
  );
  if (activeTreatmentPlan || isPlansLoading || !hasConcludedPlan || !selectedClient?.id) return '';
  if (
    hasPlanStatus(treatmentPlan, tpStatusIdByLabel, [
      TREATMENT_PLAN_V3_STATUS.STARTED,
      TREATMENT_PLAN_V3_STATUS.EDITING,
    ])
  ) {
    return 'The Client has no active Treatment Plan. Please activate one of the concluded plans.';
  }
  if (hasPlanStatus(treatmentPlan, tpStatusIdByLabel, [TREATMENT_PLAN_V3_STATUS.COMPLETED])) {
    return 'The Client has no active Treatment Plan. The BT will not be able to create session notes.';
  }
  return '';
}

export function getTpOptionsRowGates(ctx: TpOptionsRowContext): TpOptionsRowGates {
  const {
    userRoles,
    userEmail,
    providerEmail,
    treatmentPlan,
    treatmentPlans,
    activeTreatmentPlan,
    tpStatusIdByLabel,
    flags,
    isReportRunning,
  } = ctx;
  const isEditorRole = areUserRolesIn(userRoles, PLAN_EDITOR_ROLES);
  const isDeleteRole = areUserRolesIn(userRoles, PLAN_DELETE_ROLES);
  const isPlanOwner = userEmail === providerEmail;
  const hasSignature = Boolean(treatmentPlan?.signature);
  const canCreatePlan =
    areUserRolesIn(userRoles, PLAN_CREATOR_ROLES) &&
    (treatmentPlans.length === 0 ||
      hasPlanStatus(treatmentPlan, tpStatusIdByLabel, [TREATMENT_PLAN_V3_STATUS.COMPLETED]));

  return {
    isEditorRole,
    isDeleteRole,
    isPlanOwner,
    isNotEditable: hasPlanStatus(treatmentPlan, tpStatusIdByLabel, TP_NOT_EDITABLE_STATUSES),
    isCurrentPlanActive:
      treatmentPlan?.id !== undefined && treatmentPlan.id === activeTreatmentPlan?.id,
    canCreatePlan,
    canClonePlan: canCreatePlan && treatmentPlan?.id !== undefined,
    canReport:
      areUserRolesIn(userRoles, PROGRESS_REPORT_ROLES) &&
      !isReportRunning &&
      hasSignature &&
      hasPlanStatus(treatmentPlan, tpStatusIdByLabel, TP_REPORTABLE_STATUSES),
    canDownload:
      flags.showTpActions &&
      areUserRolesIn(userRoles, DOWNLOAD_TP_ROLES) &&
      hasSignature &&
      hasPlanStatus(treatmentPlan, tpStatusIdByLabel, TP_DOWNLOADABLE_STATUSES),
    canDelete:
      isDeleteRole &&
      hasPlanStatus(treatmentPlan, tpStatusIdByLabel, [TREATMENT_PLAN_V3_STATUS.STARTED]) &&
      isPlanOwner,
    showTpActions: flags.showTpActions,
    hasNoConcludedTP: flags.hasNoConcludedTP,
    hasMultiplePlans: treatmentPlans.length > 1,
  };
}

function buildCreateRows(gates: TpOptionsRowGates): TpOptionsMenuEntry[] {
  return [
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.CREATE_FROM_INTAKE,
      label: 'Create a plan from Intake',
      disabled: true,
      hasSubmenu: true,
    },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.CREATE_BLANK,
      label: 'Create a blank plan',
      disabled: !gates.canCreatePlan,
      hasSubmenu: true,
      submenuId: TP_OPTIONS_SUBMENU_IDS.BLANK_PLAN,
      testId: 'createTp',
    },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.CLONE,
      label: 'Clone TP',
      disabled: !gates.canClonePlan,
      hasSubmenu: true,
      submenuId: TP_OPTIONS_SUBMENU_IDS.CLONE_PLAN,
    },
    { type: 'divider', id: 'divider-create' },
  ];
}

function buildPlanRows(gates: TpOptionsRowGates): TpOptionsMenuEntry[] {
  const selectPlanRows: TpOptionsMenuEntry[] = gates.hasMultiplePlans
    ? [
        {
          type: 'row',
          id: TP_OPTIONS_MENU_ITEM_IDS.SELECT_PLAN,
          label: 'Select TP',
          disabled: false,
          hasSubmenu: true,
          submenuId: TP_OPTIONS_SUBMENU_IDS.SELECT_PLAN,
        },
      ]
    : [];
  return [
    ...selectPlanRows,
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.EDIT,
      label: 'Edit TP',
      disabled:
        !gates.showTpActions ||
        !gates.isEditorRole ||
        gates.isNotEditable ||
        gates.hasNoConcludedTP,
      hasSubmenu: false,
      testId: 'editTp',
    },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.DELETE,
      label: 'Delete TP',
      disabled: !gates.canDelete,
      hasSubmenu: false,
      testId: 'deleteTp',
    },
    { type: 'divider', id: 'divider-plan' },
  ];
}

function buildActivationRows(gates: TpOptionsRowGates): TpOptionsMenuEntry[] {
  return [
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.VIEW_ACTIVATION,
      label: 'View / edit activation',
      disabled: !gates.isDeleteRole || !gates.isPlanOwner,
      hasSubmenu: false,
      testId: 'viewEditActivation',
    },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.ACTIVATE,
      label: 'Activate TP',
      disabled:
        !gates.showTpActions ||
        !gates.isEditorRole ||
        gates.isCurrentPlanActive ||
        gates.isNotEditable,
      hasSubmenu: false,
      testId: 'activateTp',
    },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.DEACTIVATE,
      label: 'Deactivate TP',
      disabled:
        !gates.showTpActions ||
        !gates.isEditorRole ||
        !gates.isCurrentPlanActive ||
        gates.hasNoConcludedTP,
      hasSubmenu: false,
      testId: 'deactivateTp',
    },
    { type: 'divider', id: 'divider-activation' },
  ];
}

function buildReportRows(gates: TpOptionsRowGates): TpOptionsMenuEntry[] {
  return [
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.PROGRESS_REPORT,
      label: 'Progress report',
      disabled: !gates.canReport,
      hasSubmenu: false,
      testId: 'downloadReport',
    },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.DOWNLOAD,
      label: 'Download TP',
      disabled: !gates.canDownload,
      hasSubmenu: false,
      testId: 'downloadTp',
    },
    { type: 'divider', id: 'divider-report' },
    {
      type: 'row',
      id: TP_OPTIONS_MENU_ITEM_IDS.SEND_FOR_SIGNATURE,
      label: 'Send for signature',
      disabled: true,
      hasSubmenu: false,
    },
  ];
}

export function buildTpOptionsRows(ctx: TpOptionsRowContext): TpOptionsMenuEntry[] {
  const gates = getTpOptionsRowGates(ctx);
  return [
    ...buildCreateRows(gates),
    ...buildPlanRows(gates),
    ...buildActivationRows(gates),
    ...buildReportRows(gates),
  ];
}

export function getHeaderDialogCopy(
  dialog: HeaderDialog,
  ctx: HeaderDialogCopyContext,
): HeaderDialogCopy | null {
  const { planLabel, activePlanLabel, isReportLoading } = ctx;
  switch (dialog.kind) {
    case 'switchClient':
      return {
        content: dialog.nextClient
          ? 'Are you sure you want to change to a different client?'
          : 'Are you sure you want to clear the selected client?',
        submitText: 'CONTINUE',
        cancelText: 'BACK TO CURRENT PLAN',
      };
    case 'existingActive':
      return {
        content: `Treatment plan ${activePlanLabel} is currently active. Please deactivate before activating a new treatment plan.`,
        submitText: 'SWITCH TO ACTIVE PLAN',
        cancelText: 'CANCEL',
        submitTestId: 'submitConfirmDialog',
      };
    case 'activate':
      return {
        content: `Are you sure you want to activate treatment plan ${planLabel}?`,
        submitText: 'ACTIVATE',
        cancelText: 'CANCEL',
      };
    case 'activated':
      return {
        content: `Treatment plan ${planLabel} is now active.`,
        submitText: 'CONTINUE',
        hideCancel: true,
      };
    case 'noActivePlan':
      return { content: dialog.message, submitText: 'CLOSE', hideCancel: true };
    case 'deactivate':
      return {
        content: `Are you sure you want to deactivate treatment plan ${planLabel}?`,
        submitText: 'DEACTIVATE',
        cancelText: 'CANCEL',
        cancelTestId: 'cancelButton',
      };
    case 'edit':
      return {
        content: `Are you sure you want to edit treatment plan ${planLabel}?`,
        submitText: 'CONTINUE TO EDIT',
        cancelText: 'CANCEL',
        submitTestId: 'submitEditBtn',
        cancelTestId: 'cancelEditBtn',
      };
    case 'editSuccess':
      return {
        content: 'The treatment plan is now in edit mode. ',
        submitText: 'CONTINUE',
        hideCancel: true,
        submitTestId: 'continueBtn',
      };
    case 'download':
      return {
        content: 'Are you sure you want to download this treatment plan?',
        submitText: 'Download',
        cancelText: 'Back to treatment plan',
      };
    case 'reportConfirm':
      return {
        content: isReportLoading
          ? 'Generating report. Please wait...'
          : 'Are you sure you want to download this treatment plan report?',
        submitText: 'Download',
        cancelText: 'Back to treatment plan',
      };
    case 'switchPlan':
      return {
        content:
          'Are you sure you want to switch Treatment Plan? Any unsaved information will be lost.',
        submitText: 'Switch plan',
        cancelText: 'Cancel',
      };
    default:
      return null;
  }
}

export function hasPlanId(plan: ITreatmentPlan): plan is TreatmentPlanWithId {
  return plan.id !== undefined;
}

// The summary holds one response array per requested goal.
export function isMultiTrialSummaryEmpty(summary: ITrialSummary[][]): boolean {
  return summary.every((goalSummary) => goalSummary.every((item) => !item.targets?.length));
}
